import { AlertTriangle, CheckCircle2, Info, XCircle, X } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'error';

interface ToneSpec {
  container: string;
  icon: string;
  Icon: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  /** Read out before the message so the tone is not conveyed by color alone (§48.5). */
  prefix: string;
  live: 'polite' | 'assertive' | 'off';
}

/**
 * The four funnel banner recipes (tokens doc §7.7), each a tinted surface with a 1px
 * border of the same family and text in that family's ink. The "info" set is the
 * card-on-file / timezone note; "error" is the error banner; "warning" is the pending
 * status banner and the impersonation bar (§10.2).
 */
const TONES: Record<AlertTone, ToneSpec> = {
  info: {
    /* M11.11: `--color-info-ink` is 4.10:1 on `--color-info-subtle`. Same family,
       accessible sibling — see the note in `badge.tsx`. */
    container: 'bg-info-subtle border-info-border text-primary-accessible',
    /* Q-P15: the glyph is 16px next to 14px text on a tinted surface; #18B3EF is
       2.40:1 there, under the 3:1 non-text floor (WCAG 1.4.11). */
    icon: 'text-primary-accessible',
    Icon: Info,
    prefix: 'Information:',
    live: 'polite',
  },
  success: {
    container: 'bg-success-subtle border-success-border text-success-ink',
    icon: 'text-success',
    Icon: CheckCircle2,
    prefix: 'Success:',
    live: 'polite',
  },
  warning: {
    container: 'bg-warning-subtle border-warning-border text-warning-ink',
    icon: 'text-warning',
    Icon: AlertTriangle,
    prefix: 'Warning:',
    live: 'polite',
  },
  error: {
    container: 'bg-danger-subtle border-danger-border text-danger-ink',
    icon: 'text-danger',
    Icon: XCircle,
    prefix: 'Error:',
    live: 'assertive',
  },
};

export interface AlertProps {
  tone?: AlertTone;
  /** Bold lead-in, as in "Card on File — we'll securely store…". */
  title?: ReactNode;
  /** Renders a dismiss button on the right. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /**
   * Whether this banner is its own live region. Default `true`.
   *
   * `false` for a banner rendered **inside** a live region that already exists — the
   * toast viewport (M13.7, WCAG 4.1.3). Nesting one live region inside another makes
   * the message announced twice, and a region created in the same tick as its own text
   * is not announced at all by VoiceOver or older JAWS, which is why the toast viewport
   * owns the region and each toast inside it is plain content.
   */
  live?: boolean;
  className?: string;
  children: ReactNode;
}

export function Alert({
  tone = 'info',
  title,
  onDismiss,
  dismissLabel = 'Dismiss',
  live = true,
  className,
  children,
}: AlertProps) {
  const spec = TONES[tone];
  const { Icon } = spec;
  const liveRole = tone === 'error' ? 'alert' : 'status';

  return (
    <div
      role={live ? liveRole : undefined}
      aria-live={live ? spec.live : undefined}
      className={cn(
        'flex items-start gap-[10px] rounded-input border px-4 py-[14px] text-sm leading-[1.55]',
        spec.container,
        className,
      )}
    >
      <span className={cn('mt-[1px] shrink-0', spec.icon)}>
        <Icon size={16} strokeWidth={2} aria-hidden={true} />
      </span>
      <div className="min-w-0 flex-1">
        <span className="sr-only">{spec.prefix} </span>
        {title === undefined ? null : <span className="font-semibold">{title} </span>}
        {children}
      </div>
      {onDismiss === undefined ? null : (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-mt-[2px] -mr-1 inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-sm opacity-70 hover:opacity-100"
        >
          <X size={14} strokeWidth={2} aria-hidden={true} />
        </button>
      )}
    </div>
  );
}
